// utils/migration.jsx
import { getCurrentQuarter, getAvailableQuarters } from '../constants/quarterConfig.jsx';
import { log } from './devLog';

/**
 * Migration utilities for handling quote configuration upgrades
 * Add new migration functions here as the schema evolves
 */


export const migrateSystemType = (savedConfig, extraLogs = false) => {
  if(extraLogs) {
    log('Migrating encoding scheme:', savedConfig);
  }

  let migratedConfig = { ...savedConfig };

  if(migratedConfig.systemType === undefined || migratedConfig.systemType === '')
  {
    if(extraLogs)
    {
      log("System Type needs migration");
    }
    // Assume undefined or empty is an old old quote and migrate it to be a BaseSystem type
    migratedConfig.systemType = 'BaseSystem';
  }

  return migratedConfig;
}

/**
 * Migrates old encoding schemes from "X+Y+Z" format to "X+Y" format with separate spares
 * @param {Object} savedConfig - The configuration object to migrate
 * @returns {Object} - Migrated configuration
 */
export const migrateEncodingScheme = (savedConfig, extraLogs = false) => {
  if(extraLogs) {
    log('Migrating encoding scheme:', savedConfig);
  }

  // Check if EITHER hybrid or AFA needs migration
  const hybridNeedsMigration = savedConfig.hybridEncodingScheme &&
    savedConfig.hybridEncodingScheme.includes('+') &&
    savedConfig.hybridSpares === undefined;
    
  const afaNeedsMigration = savedConfig.afaEncodingScheme &&
    savedConfig.afaEncodingScheme.includes('+') &&
    savedConfig.afaSpares === undefined;

  const needsMigration = hybridNeedsMigration || afaNeedsMigration;

  if (needsMigration) {
    if(extraLogs) {
      log('Old format detected, migrating encoding schemes...');
    }
    
    let migratedConfig = { ...savedConfig };

    // Migrate hybrid encoding scheme if needed
    if (hybridNeedsMigration) {
      const hybridParts = savedConfig.hybridEncodingScheme.split('+');
      if (hybridParts.length === 3) {
        migratedConfig.hybridEncodingScheme = `${hybridParts[0]}+${hybridParts[1]}`;
        migratedConfig.hybridSpares = 2; // Extract the spare count
      }
    }

    // Migrate AFA encoding scheme if needed  
    if (afaNeedsMigration) {
      const afaParts = savedConfig.afaEncodingScheme.split('+');
      if (afaParts.length === 3) {
        migratedConfig.afaEncodingScheme = `${afaParts[0]}+${afaParts[1]}`;
        migratedConfig.afaSpares = 2; // Extract the spare count
      }
    }

    // Ensure other required fields
    migratedConfig.hybridMLEC = migratedConfig.hybridMLEC ?? false;

    return migratedConfig;
  }

  // No migration needed, but ensure required fields exist
  return {
    ...savedConfig,
    hybridSpares: savedConfig.hybridSpares ?? 2,
    afaSpares: savedConfig.afaSpares ?? 2,
    hybridMLEC: savedConfig.hybridMLEC ?? false
  };
};

// CONF-93. Targets are chosen so migration is BOM-neutral: 'Ethernet' already
// resolves to the Broadcom card under CCI slot 1, and 'Infiniband' to the NDR200
// card under slot 3 (= 'NDR200/200GbE'). Changing either target silently changes
// the hardware on every saved quote.
//
// Ethernet lands on '200GbE', not '100GbE' (user decision 2026-09-17). Both book
// the identical slot-1 bundle and the identical Broadcom pair, so the BOM is the
// same either way — but pre-CONF-93 Ethernet quotes reported ~60/41, which is
// 200G behaviour, and the card is described "200/100GbE Dual-Port". Sending them
// to 100GbE's 40/40 ceiling would drop the throughput on every reloaded quote.
//
// '400GbE/200GbE' is the short-lived combined id that shipped to the test server
// on 2026-09-16 before the option was split; it maps to the 400GbE half, which
// kept its port count and CCI slot 2.
const LEGACY_INTERFACE_OPTIONS = {
  Ethernet: '200GbE',
  Ethernet100: '200GbE',
  Ethernet200: '200GbE',
  Infiniband: 'NDR200/200GbE',
  '400GbE/200GbE': '400GbE',
};

/**
 * Migration function specifically for BOM generation backwards compatibility
 * Used when processing configs that may not have been migrated yet
 *
 * CONF-93. The rule is deliberately inverted: only the four KNOWN LEGACY
 * strings above are rewritten; every other value passes through unchanged.
 * Listing the current network option ids here instead would make this module a
 * second source of truth — a fifth option added to vendorRegistry.js (the
 * documented way to add one) would fall through to '100GbE' and silently ship
 * Broadcom 100GbE cards on the BOM. Pass-through is safe because
 * getNetworkOption's own fallback already handles genuinely unknown ids. An
 * absent/empty value defaults to '200GbE' — a config with no option at all has
 * to resolve to something, and it resolves the same way legacy 'Ethernet' does.
 *
 * @param {string} interfaceOption - The interface option to normalize
 * @returns {string} - Normalized interface option
 */
export const normalizeLegacyInterfaceOption = (interfaceOption) => {
  if (!interfaceOption) {
    return '200GbE';
  }
  if (Object.prototype.hasOwnProperty.call(LEGACY_INTERFACE_OPTIONS, interfaceOption)) {
    return LEGACY_INTERFACE_OPTIONS[interfaceOption];
  }
  return interfaceOption;
};

/**
 * Migrates old interface options to new simplified format
 * @param {Object} config - The configuration object to migrate
 * @returns {Object} - Migrated configuration
 */
export const migrateInterfaceOption = (config, extraLogs = false) => {
  if(extraLogs) {
    log('Migrating interface option:', config.interfaceOption);
  }

  const interfaceOption = normalizeLegacyInterfaceOption(config.interfaceOption);
  // Legacy configs have no protocol. TCP is the only transport those quotes
  // could have been measured on, so it is the honest default.
  //
  // This deliberately DIVERGES from the new-config default, which CONF-93 moved
  // to RDMA (useConfigurationLogic). Do not "align" them: defaulting a saved
  // quote to RDMA would silently report 74/50 where the customer was quoted
  // 60/40. New configs start on RDMA; old quotes keep the numbers they were
  // sized under. User decision 2026-09-16.
  const networkProtocol = config.networkProtocol || 'TCP';

  if (interfaceOption === config.interfaceOption && networkProtocol === config.networkProtocol) {
    return config;
  }

  return { ...config, interfaceOption, networkProtocol };
};

/**
 * Migrates old VPOD count property names to new Node count names
 * @param {Object} savedConfig - The configuration object to migrate
 * @returns {Object} - Migrated configuration
 */
export const migrateVpodToNodeCount = (savedConfig, extraLogs = false) => {
  if(extraLogs) {
    log('Migrating VPOD count to Node count:', savedConfig);
  }

  let migratedConfig = { ...savedConfig };
  let needsMigration = false;

  // Check if old property names exist and new ones don't
  if (savedConfig.hybridVpodCount !== undefined && savedConfig.hybridNodeCount === undefined) {
    migratedConfig.hybridNodeCount = savedConfig.hybridVpodCount;
    needsMigration = true;
  }

  if (savedConfig.afaVpodCount !== undefined && savedConfig.afaNodeCount === undefined) {
    migratedConfig.afaNodeCount = savedConfig.afaVpodCount;
    needsMigration = true;
  }

  if (needsMigration && extraLogs) {
    log('Migrated VPOD counts to Node counts');
  }

  return migratedConfig;
};

/**
 * Migrates outdated quarters to current quarter
 * @param {Object} config - The configuration object to check
 * @returns {Object} - Configuration with valid quarter
 */
export const migrateQuarter = (config, extraLogs = false) => {
  const currentQuarter = getCurrentQuarter();
  const availableQuarters = getAvailableQuarters();
  
  // If loaded quarter is not in available quarters, use current quarter
  if (!availableQuarters.includes(config.quarter)) {
    if(extraLogs) {
      log(`Quarter ${config.quarter} no longer available, migrating to ${currentQuarter}`);
    }
    return {
      ...config,
      quarter: currentQuarter
    };
  }
  
  return config;
};

const migrateAccountExecutiveInfo = (config, extraLogs = false) => {
  if(extraLogs) {
    log('Migrating account executive info');
  }
  const migratedConfig = { ...config };
  if(!migratedConfig.salesforceQuoteNum) {
    migratedConfig.salesforceQuoteNum = '00000'; // Default value
  }
  if(!migratedConfig.accountExecutive) {
    migratedConfig.accountExecutive = '';
  }
  if(!migratedConfig.accountExecutiveEmail) {
    migratedConfig.accountExecutiveEmail = '';
  }
  return migratedConfig;
}

/**
 * Master migration function that applies all migrations in sequence
 * @param {Object} savedConfig - The raw saved configuration
 * @returns {Object} - Fully migrated configuration
 */
export const migrateQuoteConfig = (savedConfig, extraLogs = false) => {
  if(extraLogs) {
    log('Starting quote migration for config:', savedConfig);
  }

  // Apply all migrations in sequence
  let migratedConfig = savedConfig;

  migratedConfig = migrateSystemType(migratedConfig);
  migratedConfig = migrateVpodToNodeCount(migratedConfig);
  migratedConfig = migrateQuarter(migratedConfig);
  migratedConfig = migrateEncodingScheme(migratedConfig);
  migratedConfig = migrateInterfaceOption(migratedConfig);
  migratedConfig = migrateAccountExecutiveInfo(migratedConfig);
  migratedConfig.sizeByNodeCount = migratedConfig.sizeByNodeCount ?? false;
  // CONF-210: afaNodeModel is deliberately NOT defaulted here. Absent already
  // means the single-CPU flash node everywhere it is read, and stamping
  // 'standard' would move the frozen initial-config / after-load snapshots.
  // loadConfig clears a stale session value instead (useConfigurationLogic).

  // Future migrations can be added here as new steps
  // migratedConfig = migrateNewFeature(migratedConfig);
  
  if(extraLogs) {
    log('Migration complete:', migratedConfig);
  }
  return migratedConfig;
};

